import { useEffect, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import gsap from 'gsap';
import * as THREE from 'three';

function CameraController({
  shouldAnimate,
  startExperience,
  cameraCoords,
  cameraHeight,
  stationIndex,
  totalStations,
  setIsTransitioning,
  setVideoVisible,
  resetAppState
}) {
  const { camera } = useThree();
  const isAnimating = useRef(false);
  const hasInitialAnimationRun = useRef(false);
  const initialCameraPosition = useRef(new THREE.Vector3(65, 50, 110));
  const initialLookAtPosition = useRef(new THREE.Vector3(65, 0, -10));
  const initialCameraQuaternion = useRef(new THREE.Quaternion());
  const initialCameraRotation = useRef(new THREE.Euler());
  
  const previousStationIndex = useRef(null);
  const initialFOV = useRef(75);
  const [hasResetOccurred, setHasResetOccurred] = useState(false);

  const postIntroPosition = useRef(new THREE.Vector3(0, 20, 0));
  const postIntroQuaternion = useRef(new THREE.Quaternion(-0.049814, 0, 0, 0.998759));

  useEffect(() => {
    console.log("CameraController props updated:", { 
      shouldAnimate, 
      startExperience, 
      cameraCoords, 
      cameraHeight, 
      stationIndex 
    });
  }, [shouldAnimate, startExperience, cameraCoords, cameraHeight, stationIndex]);

  function setupCamera() {
    console.log("Setting up camera");
    camera.position.copy(initialCameraPosition.current);
    camera.lookAt(initialLookAtPosition.current);
    camera.fov = initialFOV.current;
    camera.near = 0.1;
    camera.far = 1000;
    camera.updateProjectionMatrix();
    previousStationIndex.current = null;
    initialCameraQuaternion.current.copy(camera.quaternion);
    initialCameraRotation.current.copy(camera.rotation);
    console.log("Initial setup - Position:", camera.position.toArray());
    console.log("Initial setup - Rotation:", camera.rotation.toArray());
    console.log("Initial setup - Quaternion:", camera.quaternion.toArray());
  }

  useEffect(() => {
    if (camera) {
      setupCamera();
    }
  }, [camera]);

  useEffect(() => {
    if (shouldAnimate && camera && !hasInitialAnimationRun.current) {
      animateInitialMovement();
    }
  }, [shouldAnimate, camera]);

  useEffect(() => {
    if (startExperience && camera) {
      console.log("startExperience triggered, animating to station");
      animateToStation();
    }
  }, [startExperience, camera, cameraCoords, cameraHeight, stationIndex, totalStations]);

  function animateInitialMovement() {
    console.log("Animating initial movement");
    isAnimating.current = true;

    const timeline = gsap.timeline({
      onUpdate: () => {
        camera.updateProjectionMatrix();
      },
      onComplete: () => {
        isAnimating.current = false;
        hasInitialAnimationRun.current = true;
        postIntroPosition.current.copy(camera.position);
        postIntroQuaternion.current.copy(camera.quaternion);
        setVideoVisible(true);
        console.log("Initial animation complete. Camera position:", camera.position.toArray());
      },
    });

    timeline.to(camera.position, {
      duration: 4,
      x: postIntroPosition.current.x,
      y: postIntroPosition.current.y,
      z: postIntroPosition.current.z,
      ease: 'power2.out',
    }, 0);

    timeline.to(camera.quaternion, {
      duration: 4,
      x: postIntroQuaternion.current.x,
      y: postIntroQuaternion.current.y,
      z: postIntroQuaternion.current.z,
      w: postIntroQuaternion.current.w,
      ease: 'power2.out',
    }, 0);

    return () => timeline.kill();
  }

  function animateToStation() {
    console.log("Animating to station. Camera coords:", cameraCoords, "Camera height:", cameraHeight);
    
    if (!cameraCoords || (Array.isArray(cameraCoords) && cameraCoords.length === 0)) {
      console.error('Invalid or empty cameraCoords:', cameraCoords);
      return;
    }

    let coordsArray;
    if (typeof cameraCoords === 'string') {
      coordsArray = cameraCoords.split(',').map(Number);
    } else if (Array.isArray(cameraCoords)) {
      coordsArray = cameraCoords;
    } else {
      console.error('Invalid cameraCoords type:', typeof cameraCoords);
      return;
    }

    if (coordsArray.length !== 3 || coordsArray.some(isNaN)) {
      console.error('Invalid cameraCoords values:', coordsArray);
      return;
    }

    isAnimating.current = true;
    setIsTransitioning(true);

    const [rotX, rotY, rotZ] = coordsArray;
    const targetQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(rotX, rotY, rotZ, 'YXZ'));
    const isFirstTransition = previousStationIndex.current === null || hasResetOccurred;
    const isFar = isFarTransition();

    console.log("Animating to station. Is first transition:", isFirstTransition, "Has reset occurred:", hasResetOccurred);

    const timeline = gsap.timeline({
      onUpdate: () => camera.updateProjectionMatrix(),
      onComplete: () => {
        isAnimating.current = false;
        previousStationIndex.current = stationIndex;
        setIsTransitioning(false);
        setHasResetOccurred(false);
        console.log("Station animation complete. New camera state:", {
          position: camera.position.toArray(),
          rotation: camera.rotation.toArray(),
          fov: camera.fov
        });
      },
    });

    const duration = isFirstTransition || isFar ? 5 : 4;
    const ease = 'power2.inOut';

    timeline.to(camera.position, { duration, x: 0, y: cameraHeight, z: 0, ease }, 0);
    timeline.to(camera.quaternion, { duration, x: targetQuaternion.x, y: targetQuaternion.y, z: targetQuaternion.z, w: targetQuaternion.w, ease }, 0);

    if (isFirstTransition || isFar) {
      console.log("Animating FOV from", camera.fov, "to 11");
      timeline.to(camera, { duration, fov: 11, ease }, 0);
    } else {
      timeline.to(camera, { duration: duration / 2, fov: 50, ease }, 0);
      timeline.to(camera, { duration: duration / 2, fov: 11, ease }, duration / 2);
    }

    return () => timeline.kill();
  }

  function isFarTransition() {
    if (previousStationIndex.current === null) return false;
    const indexDifference = Math.abs(stationIndex - previousStationIndex.current);
    const wrappedDifference = Math.min(indexDifference, totalStations - indexDifference);
    return wrappedDifference > 1;
  }

  function resetCamera() {
    console.log("Resetting camera");
    isAnimating.current = true;
    setIsTransitioning(true);

    const timeline = gsap.timeline({
      onUpdate: () => camera.updateProjectionMatrix(),
      onComplete: () => {
        console.log("Reset animation complete");
        console.log("After reset - Position:", camera.position.toArray());
        console.log("After reset - Rotation:", camera.rotation.toArray());
        console.log("After reset - Quaternion:", camera.quaternion.toArray());
        console.log("After reset - FOV:", camera.fov);
        isAnimating.current = false;
        hasInitialAnimationRun.current = false;
        previousStationIndex.current = null;
        setIsTransitioning(false);
        setHasResetOccurred(true);
        resetAppState();
      },
    });

    timeline.to(camera.position, {
      duration: 4,
      x: initialCameraPosition.current.x,
      y: initialCameraPosition.current.y,
      z: initialCameraPosition.current.z,
      ease: 'power1.out',
    }, 0);

    timeline.to(camera.quaternion, {
      duration: 4,
      x: initialCameraQuaternion.current.x,
      y: initialCameraQuaternion.current.y,
      z: initialCameraQuaternion.current.z,
      w: initialCameraQuaternion.current.w,
      ease: 'power1.out',
    }, 0);

    timeline.to(camera, {
      duration: 1,
      fov: initialFOV.current,
      ease: 'power1.out',
    }, 0);

    return () => timeline.kill();
  }

  useEffect(() => {
    if (!shouldAnimate && !startExperience && hasInitialAnimationRun.current) {
      resetCamera();
    }
  }, [shouldAnimate, startExperience]);

  return null;
}

export default CameraController;